import { NgModule } from '@angular/core';
import { CanMatchFn, RouterModule, Routes, UrlSegment } from '@angular/router';
import { titleChangemetaData as SERVICE_PAGES } from './service/constant';
import { INDIA_PORTS } from './ship-from/india-ports';
import { GUIDES } from './guides/guides-data';
import { airportResolver } from './airport-codes/airport-resolver';
import { productResolver } from './export-to-usa/product-resolver';
import { laneResolver } from './lanes/lane-resolver';
import { sealanesHubResolver, sealanesServiceResolver, sealanesVesselResolver } from './india-usa-sealanes/sealanes-resolvers';
import { locationResolver } from './india-port-codes/location-resolver';
import { NavbarComponent } from './navbar/navbar.component';

// A parameterised route only matches a slug it can render; anything else falls through to the 404 route.
const slugIn = (allowed: string[]): CanMatchFn =>
  (_route, segments: UrlSegment[]) => allowed.includes(segments[1]?.path);

// The same check for large page families: the index is imported only when a URL in that section is
// requested, so it stays out of the main bundle. `test` gets the URL segments.
const matchLazy = <T>(load: () => Promise<T>, test: (mod: T, segments: UrlSegment[]) => boolean): CanMatchFn =>
  (_route, segments: UrlSegment[]) => load().then(mod => test(mod, segments));

const routes: Routes = [
  // Before the site shell: its catch-all 404 route would otherwise swallow these.
  {
    // portal vessel schedule (was eagerly imported in AppModule); loaded only when visited
    path: 'vessel-schedule/:origin/:destination',
    loadChildren: () => import('../../../portal/src/app/vessel-schedule/vessel-schedule.module').then(m => m.VesselScheduleModule)
  },
  {
    path: "auth",
    loadChildren: () => import('../../../shared/src/app/auth/auth.module').then(m => m.AuthModule)
  },
  { path: '', redirectTo: '', pathMatch: 'full' },
  {
    path: "",
    component: NavbarComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./landing-page/landing-page.component').then((c) => c.LandingPageComponent),
      },
      {
        path: "products/:value",
        loadComponent: () =>
          import('./product-screen/product-screen.component').then((c) => c.ProductScreenComponent),
      },
      {
        path: "services/:tabName",
        canMatch: [slugIn(Object.keys(SERVICE_PAGES))],
        loadComponent: () => import('./service/ocean-fcl.component').then(m => m.OceanFclComponent)
      },
      {
        path: "contact-us",
        loadComponent: () => import('./contact/contact.component').then(m => m.ContactComponent)
      },
      {
        path: "legal/:tabName",
        canMatch: [slugIn(['privacy-policy', 'terms-of-service'])],
        loadComponent: () => import('./privacy-policy/privacy-policy.component').then(m => m.PrivacyPolicyComponent)
      },
      // {
      //   path: "discovery/rate-search/:from/:to",
      //   loadComponent: () =>
      //     import('./discovery-rate-main/discovery-rate-main.component').then((c) => c.DiscoveryRateMainComponent),
      // },
      {
        path: "discovery",
        loadChildren: () => import('../../../shared/src/app/rate-search/rate-search.module').then(m => m.RateSearchModule)
      },
            {
        path: "trucking",
        loadChildren: () => import('./trucking/trucking.module').then(m => m.TruckingModule)
      },
      {
        path: "tools",
        loadChildren: () => import('./tools/tools.module').then(m => m.ToolsModule)
      },
      {
        path: "resources",
        loadChildren: () => import('./../app/resources/resources.module').then(m => m.ResourcesModule)
      },
      {
        path: "port-to-port",
        loadChildren: () => import('./tools/tools.module').then(m => m.ToolsModule),
         data: { isPortToPort: true }
      },
      {
        path: 'guides',
        loadComponent: () => import('./guides/guide.component').then(m => m.GuideComponent)
      },
      {
        path: 'guides/:slug',
        canMatch: [slugIn(GUIDES.map(g => g.slug))],
        loadComponent: () => import('./guides/guide.component').then(m => m.GuideComponent)
      },
      {
        path: 'freight-forwarder-chennai',
        loadComponent: () => import('./ship-from/chennai-office.component').then(m => m.ChennaiOfficeComponent)
      },
      {
        path: 'freight-forwarder-india-to-usa',
        data: { forwarderPage: 'freight-forwarder-india-to-usa' },
        loadComponent: () => import('./freight-forwarder/forwarder-page.component').then(m => m.ForwarderPageComponent)
      },
      {
        path: 'freight-forwarder-usa',
        data: { forwarderPage: 'freight-forwarder-usa' },
        loadComponent: () => import('./freight-forwarder/forwarder-page.component').then(m => m.ForwarderPageComponent)
      },
      {
        path: 'ship-from',
        loadComponent: () => import('./ship-from/ship-from.component').then(m => m.ShipFromComponent)
      },
      {
        path: 'ship-from/:port',
        canMatch: [slugIn(INDIA_PORTS.map(p => p.slug))],
        loadComponent: () => import('./ship-from/ship-from.component').then(m => m.ShipFromComponent)
      },
      {
        path: 'airport-codes',
        loadComponent: () => import('./airport-codes/airport-codes.component').then(m => m.AirportCodesComponent)
      },
      {
        // /airport-codes/bom-mumbai: only airports the generator published
        path: 'airport-codes/:airport',
        canMatch: [matchLazy(() => import('./airport-codes/airport-slugs'), (m, s) => m.AIRPORT_SLUGS.includes(s[1]?.path))],
        loadComponent: () => import('./airport-codes/airport.component').then(m => m.AirportComponent),
        resolve: { airport: airportResolver }
      },
      {
        path: 'export-to-usa',
        loadComponent: () => import('./export-to-usa/export-to-usa.component').then(m => m.ExportToUsaComponent)
      },
      {
        // /export-to-usa/shrimp-and-crustaceans-0306: only products the generator published
        path: 'export-to-usa/:product',
        canMatch: [matchLazy(() => import('./export-to-usa/product-index'), (m, s) => m.PRODUCT_INDEX.some(p => p.slug === s[1]?.path))],
        loadComponent: () => import('./export-to-usa/product.component').then(m => m.ProductComponent),
        resolve: { product: productResolver }
      },
      {
        // /ship-from/chennai/to-new-york: only lanes the generator published (enough sailings to say something)
        path: 'ship-from/:port/:lane',
        canMatch: [matchLazy(() => import('./lanes/lane-copy'), (m, s) => !!m.findLaneEntry(s[1]?.path, s[2]?.path))],
        loadComponent: () => import('./lanes/lane.component').then(m => m.LaneComponent),
        resolve: { lane: laneResolver }
      },
      {
        path: 'ship-to',
        loadComponent: () => import('./lanes/ship-to.component').then(m => m.ShipToComponent)
      },
      {
        path: 'ship-to/:place',
        canMatch: [matchLazy(() => import('./lanes/lane-index'), (m, s) => m.LANE_INDEX.some(l => l.dest === s[1]?.path))],
        loadComponent: () => import('./lanes/ship-to.component').then(m => m.ShipToComponent)
      },
      {
        // India–USA Sealanes: weekly snapshot of carrier schedules (scripts/generate-india-usa-sealanes.mjs)
        path: 'india-usa-sealanes',
        loadComponent: () => import('./india-usa-sealanes/sealanes-hub.component').then(m => m.SealanesHubComponent),
        resolve: { hub: sealanesHubResolver }
      },
      {
        path: 'india-usa-sealanes/services/:slug',
        canMatch: [matchLazy(() => import('./india-usa-sealanes/sealanes-copy'), (m, s) => !!m.serviceChunkFor(s[2]?.path))],
        loadComponent: () => import('./india-usa-sealanes/sealanes-service.component').then(m => m.SealanesServiceComponent),
        resolve: { service: sealanesServiceResolver }
      },
      {
        path: 'india-usa-sealanes/vessels/:slug',
        canMatch: [matchLazy(() => import('./india-usa-sealanes/sealanes-copy'), (m, s) => m.hasVessel(s[2]?.path))],
        loadComponent: () => import('./india-usa-sealanes/sealanes-vessel.component').then(m => m.SealanesVesselComponent),
        resolve: { vessel: sealanesVesselResolver }
      },
      {
        path: 'india-port-codes',
        loadComponent: () => import('./india-port-codes/india-port-codes.component').then(m => m.IndiaPortCodesComponent)
      },
      {
        // /india-port-codes/intkd-tughlakabad: only locations the generator published
        path: 'india-port-codes/:location',
        canMatch: [matchLazy(() => import('./india-port-codes/location-index'), (m, s) => m.LOCATION_INDEX.some(l => l.slug === s[1]?.path))],
        loadComponent: () => import('./india-port-codes/location.component').then(m => m.LocationComponent),
        resolve: { location: locationResolver }
      },
      {
        path: "about-us",
        loadComponent: () => import('./company/about-us/about-us.component').then(m => m.AboutUsComponent)
      },
      // {
      //   path: "company",
      //   redirectTo: "about-us",
      //   pathMatch: 'full'
      // },
      // {
      //   path: "company/about-us",
      //   redirectTo: "about-us",
      //   pathMatch: 'full'
      // },
      // {
      //   path: 'vessel-schedule',
      //   loadChildren: () => import('../../../portal/src/app/vessel-schedule/vessel-schedule.module').then(m => m.VesselScheduleModule)
      // },
      {
        path: 'vessel-schedules',
        loadChildren: () => import('../../../shared/src/app/schedule/schedule.module').then(m => m.ScheduleModule),
        // website-only look for the shared schedule screens (the portal's /schedule keeps its own)
        data: { websiteDesign: true }
      },
      {
        path: 'tariff-simulator',
        loadComponent: () => import('./tariff-simulator/tariff-simulator.component').then(m => m.TariffSimulatorComponent)
      },
      { path: 'hs-code',
        loadComponent: () => import('./hts-lookup/hts-lookup.component').then(m =>m.HtsLookupComponent )
      },
      {
        path: 'tariff-comparison',
        loadComponent: () => import('./tariff-comparison/tariff-comparison.component').then(m => m.TariffComparisonComponent)

      },
      {
        path: 'sso-sync',
        loadComponent: () => import('./sso-sync/sso-sync.component').then(m => m.SsoSyncComponent)
      },
      // {
      //   path: 'main',
      //   loadComponent: () => import('./main-redirect/main-redirect.component').then(m => m.MainRedirectComponent)
      // }
      // the shared login falls back to /main (the portal's dashboard) when there's no redirectUrl;
      // the website has no such page, so without this the navigation failed and login looked like it did nothing
      {
        path: 'main',
        pathMatch: 'prefix',
        redirectTo: '/'
      },
      {
        path: '**',
        loadComponent: () => import('./not-found/not-found.component').then(m => m.NotFoundComponent)
      }

    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { initialNavigation: 'enabledBlocking' })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
